import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Users,
  Leaf,
  Shield,
  Book,
  Music,
  Home,
  DollarSign,
  Map as MapIcon,
  Calendar,
  Scroll,
  Wrench,
  X,
  Hammer,
  Droplets,
  Radio,
  Flame,
  Truck,
  Pickaxe,
  Scale,
  Compass,
  Trees,
  Shirt,
  PawPrint,
  Landmark,
  Archive,
  Stethoscope,
  ClipboardList,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { MINISTRY_DEFINITIONS } from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ministryIconMap = {
  elders: Users,
  counsel: Book,
  overseers: ClipboardList,
  right_ruling_mediation: Scale,
  records_genealogy_chronicles: Archive,

  yadah: Music,

  treasury_stewards: DollarSign,
  internal_exchange_weights_measures: Scale,
  land_stewards: Landmark,
  crop_seed_storehouse: Leaf,
  animal_husbandry_fishing_storehouse: PawPrint,
  water_stewards: Droplets,

  health_healing_birth_infant: Stethoscope,

  warfare_watchmen: Shield,
  shamarym: Shield,

  research_discovery_scroll_house: Scroll,

  feast_appointments: Calendar,
  outreach_mission: MapIcon,

  marriage: Users,
  singles: Users,
  transitioners_covenant_prep: Home,

  ban_yahuah: Users,
  bat_yahuah: Users,
  youth: Users,
  mens: Users,
  womens: Users,

  hospitality_guest_care: Home,

  communications_media_technology: Radio,
  infrastructure_transportation_emergency: Truck,
  off_grid_energy_fuel: Flame,
  tools_mechanical: Wrench,

  metal_workers: Hammer,
  stone_masons: Hammer,
  forestry_woodworkers_carpentry: Trees,
  textile_garment: Shirt,
  leather_workers: Shirt,
  potters_clay: Hammer,
  manufacturing_housewares: Wrench,

  malach_minerals_mining: Pickaxe,
  sanitation_cleanliness: Droplets,
  burial_mourning: Archive,
  pilots_captains: Compass,
};

const programs = MINISTRY_DEFINITIONS.map((ministry) => ({
  ...ministry,
  name: ministry.label,
  icon: ministryIconMap[ministry.key] || Users,
}));

const programLookup = programs.reduce((acc, program) => {
  acc[program.key] = program;
  return acc;
}, {});

const orderFlow = [
  {
    key: "elders",
    title: "Elders",
    description:
      "Guard direction, maturity, weightier matters, and the larger order of the people.",
    icon: Users,
  },
  {
    key: "counsel",
    title: "Counsel",
    description:
      "Weigh matters, advise wisely, and help decisions move with care instead of confusion.",
    icon: Book,
  },
  {
    key: "overseers",
    title: "Paqadym",
    description:
      "Carry out oversight, assignment, follow-through, and practical coordination.",
    icon: ClipboardList,
  },
  {
    key: "right_ruling_mediation",
    title: "Right-Ruling and Mediation",
    description:
      "Handle correction, restoration, order, and resolution when matters arise.",
    icon: Scale,
  },
  {
    key: "records_genealogy_chronicles",
    title: "Saparym",
    description:
      "Preserve records, chronicles, writings, rulings, testimonies, and continuity.",
    icon: Archive,
  },
];

const scripturalPatterns = [
  {
    reference: "Shamut 18:21-26",
    title: "Ordered oversight",
    description:
      "Capable men were placed over thousands, hundreds, fifties, and tens; harder matters moved upward.",
  },
  {
    reference: "Dabarym 1:13-17",
    title: "Known, wise, understanding men",
    description:
      "The people were not left scattered; mature men were selected and placed over the people by order.",
  },
  {
    reference: "BaRaSHYT 41:34-36",
    title: "Paqadym and provision",
    description:
      "Paqadym were set over the land to gather, store, and prepare provision before famine came.",
  },
  {
    reference: "Dabarym 31:24-26",
    title: "Written witness and records",
    description:
      "The scroll was written, completed, preserved, and kept as witness for the people.",
  },
];

const quickFilters = [
  "leadership",
  "household",
  "youth",
  "healing",
  "food",
  "land",
  "trade",
  "media",
  "defense",
  "logistics",
];

const programTags = {
  elders: ["leadership"],
  counsel: ["leadership"],
  overseers: ["leadership", "logistics"],
  right_ruling_mediation: ["leadership", "household"],
  records_genealogy_chronicles: ["media", "leadership"],

  yadah: ["household", "media"],
  feast_appointments: ["household", "logistics"],
  outreach_mission: ["household", "media"],
  marriage: ["household"],
  singles: ["household"],
  transitioners_covenant_prep: ["household"],
  mens: ["household", "leadership"],
  womens: ["household", "leadership"],
  hospitality_guest_care: ["household", "logistics"],

  research_discovery_scroll_house: ["media"],
  communications_media_technology: ["media", "logistics"],

  treasury_stewards: ["leadership", "logistics"],
  internal_exchange_weights_measures: ["trade", "logistics"],
  land_stewards: ["land", "food"],
  malach_minerals_mining: ["land", "trade"],

  crop_seed_storehouse: ["food", "land"],
  animal_husbandry_fishing_storehouse: ["food", "land"],
  water_stewards: ["food", "land", "logistics"],

  health_healing_birth_infant: ["healing", "household"],
  sanitation_cleanliness: ["healing", "logistics"],
  burial_mourning: ["healing", "household"],

  warfare_watchmen: ["defense", "leadership"],
  shamarym: ["defense", "logistics"],

  infrastructure_transportation_emergency: ["logistics", "defense"],
  off_grid_energy_fuel: ["logistics", "land"],
  pilots_captains: ["logistics"],

  tools_mechanical: ["trade", "logistics"],
  metal_workers: ["trade"],
  stone_masons: ["trade", "land"],
  forestry_woodworkers_carpentry: ["trade", "land"],
  textile_garment: ["trade", "household"],
  leather_workers: ["trade"],
  potters_clay: ["trade"],
  manufacturing_housewares: ["trade", "logistics"],

  ban_yahuah: ["youth", "household"],
  bat_yahuah: ["youth", "household"],
  youth: ["youth", "household"],
};

const categoryDefinitions = [
  {
    id: "covenant-life",
    title: "Covenant Life, Gatherings, and Household Strengthening",
    shortTitle: "Covenant Life",
    icon: Home,
    color: "#7c2d12",
    softColor: "#fff7ed",
    tags: ["household", "leadership", "logistics"],
    description:
      "Gatherings, household order, support, hospitality, yadah, and covenantal living among the people.",
    keys: [
      "yadah",
      "feast_appointments",
      "outreach_mission",
      "marriage",
      "singles",
      "transitioners_covenant_prep",
      "mens",
      "womens",
      "hospitality_guest_care",
    ],
  },
  {
    id: "knowledge-scrolls",
    title: "Knowledge, Scrolls, and Communication",
    shortTitle: "Knowledge & Scrolls",
    icon: Scroll,
    color: "#1d4ed8",
    softColor: "#eff6ff",
    tags: ["media", "leadership"],
    description:
      "Teaching, research, records, media, communication, writings, and clear public messaging.",
    keys: ["research_discovery_scroll_house", "communications_media_technology"],
  },
  {
    id: "stewardship-land",
    title: "Stewardship, Land, and Internal Exchange",
    shortTitle: "Stewardship & Land",
    icon: Landmark,
    color: "#854d0e",
    softColor: "#fefce8",
    tags: ["land", "trade", "logistics", "leadership"],
    description:
      "Land, treasury, minerals, internal exchange, weights, measures, and careful resource order.",
    keys: [
      "treasury_stewards",
      "internal_exchange_weights_measures",
      "land_stewards",
      "malach_minerals_mining",
    ],
  },
  {
    id: "food-agriculture",
    title: "Food, Agriculture, and Provision",
    shortTitle: "Food & Provision",
    icon: Leaf,
    color: "#15803d",
    softColor: "#f0fdf4",
    tags: ["food", "land", "logistics"],
    description:
      "Growing, storing, preserving, water care, animals, fishing, and provision for the people.",
    keys: [
      "crop_seed_storehouse",
      "animal_husbandry_fishing_storehouse",
      "water_stewards",
    ],
  },
  {
    id: "healing-care",
    title: "Healing, Cleanliness, and Care",
    shortTitle: "Healing & Care",
    icon: Stethoscope,
    color: "#0f766e",
    softColor: "#f0fdfa",
    tags: ["healing", "household", "logistics"],
    description:
      "Healing support, birth care, infant care, cleanliness, mourning, burial, and practical care.",
    keys: [
      "health_healing_birth_infant",
      "sanitation_cleanliness",
      "burial_mourning",
    ],
  },
  {
    id: "defense-guarding",
    title: "Defense, Guarding, and Preparedness",
    shortTitle: "Defense & Guarding",
    icon: Shield,
    color: "#991b1b",
    softColor: "#fef2f2",
    tags: ["defense", "leadership", "logistics"],
    description:
      "Watchfulness, guarding, warning, readiness, and protection of the people.",
    keys: ["warfare_watchmen", "shamarym"],
  },
  {
    id: "infrastructure-movement",
    title: "Infrastructure, Movement, and Continuity Systems",
    shortTitle: "Infrastructure",
    icon: Truck,
    color: "#334155",
    softColor: "#f8fafc",
    tags: ["logistics", "land", "defense"],
    description:
      "Transportation, emergency planning, energy, fuel, movement, and continuity systems.",
    keys: [
      "infrastructure_transportation_emergency",
      "off_grid_energy_fuel",
      "pilots_captains",
    ],
  },
  {
    id: "tools-works",
    title: "Tools, Works, and Productive Skill Bodies",
    shortTitle: "Tools & Works",
    icon: Hammer,
    color: "#6b21a8",
    softColor: "#faf5ff",
    tags: ["trade", "logistics", "land"],
    description:
      "Tools, repairs, trades, garments, clay, wood, stone, metal, leather, and productive skill work.",
    keys: [
      "tools_mechanical",
      "metal_workers",
      "stone_masons",
      "forestry_woodworkers_carpentry",
      "textile_garment",
      "leather_workers",
      "potters_clay",
      "manufacturing_housewares",
    ],
  },
  {
    id: "youth-household",
    title: "Youth and Household Preparation",
    shortTitle: "Youth Preparation",
    icon: Leaf,
    color: "#0369a1",
    softColor: "#f0f9ff",
    tags: ["youth", "household"],
    description:
      "Preparation for young ones, household skill, discipline, service, safety, and future responsibility.",
    keys: ["ban_yahuah", "bat_yahuah", "youth"],
  },
];

const fitOptions = [
  {
    categoryId: "covenant-life",
    title: "I help with gatherings, household order, hospitality, or yadah.",
    tag: "household",
  },
  {
    categoryId: "knowledge-scrolls",
    title: "I teach, research, write, archive, design, speak, or handle media.",
    tag: "media",
  },
  {
    categoryId: "stewardship-land",
    title: "I handle land, resources, treasury, trade, minerals, or exchange.",
    tag: "land",
  },
  {
    categoryId: "food-agriculture",
    title: "I grow food, save seed, care for animals, fish, water, or storehouse.",
    tag: "food",
  },
  {
    categoryId: "healing-care",
    title: "I support healing, birth care, infant care, cleanliness, or mourning.",
    tag: "healing",
  },
  {
    categoryId: "defense-guarding",
    title: "I guard, watch, warn, prepare, protect, or plan readiness.",
    tag: "defense",
  },
  {
    categoryId: "infrastructure-movement",
    title: "I help with transport, movement, energy, fuel, or emergency systems.",
    tag: "logistics",
  },
  {
    categoryId: "tools-works",
    title: "I build, repair, sew, craft, work tools, wood, stone, metal, or clay.",
    tag: "trade",
  },
  {
    categoryId: "youth-household",
    title: "I help prepare young ones with skill, discipline, service, and order.",
    tag: "youth",
  },
];

function getProgramTags(programKey, categoryTags = []) {
  return Array.from(
    new Set([...(programTags[programKey] || []), ...categoryTags])
  );
}

function scrollToId(id) {
  setTimeout(() => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, 75);
}

function ProgramInterestModal({ program, isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email) {
      toast.error("Please fill in required fields");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/program-interest`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        program_name: program.name,
        message: formData.message,
      });

      toast.success("Interest submitted! We'll be in touch.");
      onClose();
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !program) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h3 className="font-heading text-xl font-bold text-royal-blue">
            Join {program.name}
          </h3>

          <button
            onClick={onClose}
            className="rounded p-2 hover:bg-slate-100"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div>
            <Label className="form-label">Full Name *</Label>
            <Input
              value={formData.full_name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, full_name: e.target.value }))
              }
              className="form-input"
              placeholder="Your name"
              style={{ fontSize: "16px" }}
            />
          </div>

          <div>
            <Label className="form-label">Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((p) => ({ ...p, email: e.target.value }))
              }
              className="form-input"
              placeholder="your@email.com"
              style={{ fontSize: "16px" }}
            />
          </div>

          <div>
            <Label className="form-label">Phone</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) =>
                setFormData((p) => ({ ...p, phone: e.target.value }))
              }
              className="form-input"
              placeholder="Your phone number"
              style={{ fontSize: "16px" }}
            />
          </div>

          <div>
            <Label className="form-label">Message</Label>
            <Textarea
              value={formData.message}
              onChange={(e) =>
                setFormData((p) => ({ ...p, message: e.target.value }))
              }
              className="form-input min-h-[80px]"
              placeholder="Tell us about your interest, skills, and experience"
              style={{ fontSize: "16px" }}
            />
          </div>

          <Button type="submit" className="w-full btn-gold" disabled={loading}>
            {loading ? "Submitting..." : "Submit Interest"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Programs() {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());

  const orderPrograms = useMemo(() => {
    return orderFlow
      .map((item) => ({
        ...item,
        program: programLookup[item.key],
      }))
      .filter((item) => item.program);
  }, []);

  const categories = useMemo(() => {
    return categoryDefinitions.map((category) => ({
      ...category,
      programs: category.keys
        .map((key) => programLookup[key])
        .filter(Boolean)
        .map((program) => ({
          ...program,
          filterTags: getProgramTags(program.key, category.tags),
        })),
    }));
  }, []);

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return categories
      .map((category) => {
        const categoryText = [
          category.title,
          category.shortTitle,
          category.description,
          ...category.tags,
        ]
          .join(" ")
          .toLowerCase();

        const categoryMatchesSearch = !query || categoryText.includes(query);
        const categoryMatchesFilter =
          activeCategory === "all" || activeCategory === category.id;

        if (!categoryMatchesFilter) return null;

        const visiblePrograms = category.programs.filter((program) => {
          const programText = [
            program.name,
            program.description,
            program.key,
            ...(program.filterTags || []),
          ]
            .join(" ")
            .toLowerCase();

          const programMatchesSearch =
            !query || categoryMatchesSearch || programText.includes(query);

          const programMatchesTag =
            activeTag === "all" ||
            category.tags.includes(activeTag) ||
            program.filterTags.includes(activeTag);

          return programMatchesSearch && programMatchesTag;
        });

        if (visiblePrograms.length === 0) return null;

        return {
          ...category,
          visiblePrograms,
        };
      })
      .filter(Boolean);
  }, [categories, search, activeTag, activeCategory]);

  const allVisibleExpanded =
    filteredCategories.length > 0 &&
    filteredCategories.every((category) => expandedCategories.has(category.id));

  const hasActiveFilters =
    Boolean(search) || activeTag !== "all" || activeCategory !== "all";

  const toggleCategory = (categoryId) => {
    setExpandedCategories((current) => {
      const next = new Set(current);

      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }

      return next;
    });
  };

  const expandVisible = () => {
    setExpandedCategories(
      new Set(filteredCategories.map((category) => category.id))
    );
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  const clearFilters = () => {
    setSearch("");
    setActiveTag("all");
    setActiveCategory("all");
    setExpandedCategories(new Set());
  };

  const applyTagFilter = (tag) => {
    setSearch("");
    setActiveTag(tag);
    setActiveCategory("all");
    setExpandedCategories(new Set());
    scrollToId("quick-filters");
  };

  const chooseFit = (categoryId, tag) => {
    setSearch("");
    setActiveTag(tag);
    setActiveCategory(categoryId);
    setExpandedCategories(new Set([categoryId]));
    scrollToId(categoryId);
  };

  const jumpToCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setActiveTag("all");
    setExpandedCategories(new Set([categoryId]));
    scrollToId(categoryId);
  };

  return (
    <div className="min-h-screen bg-parchment text-slate-900">
      {/* Hero */}
      <section className="relative overflow-hidden bg-royal-blue py-20 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-royal-blue via-blue-900 to-slate-950" />
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-kingdom-gold/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="mb-4 inline-flex rounded-full border border-kingdom-gold/60 bg-white/10 px-4 py-2 text-sm font-bold text-kingdom-gold backdrop-blur">
            Programs • Functions • Service Paths
          </p>

          <h1 className="heading-hero mb-4 mt-2">Choose your place</h1>

          <p className="max-w-2xl text-2xl font-semibold text-kingdom-gold">
            Where do you feel drawn to serve?
          </p>

          <p className="body-large mt-6 max-w-3xl text-slate-200">
            See the scriptural order, choose a branch, then open the functions
            inside that branch to find where your skill, readiness, and service
            fit.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#where-do-i-fit"
              className="rounded-full bg-kingdom-gold px-6 py-3 font-bold text-white shadow-lg transition hover:opacity-90"
            >
              Help Me Find My Place
            </a>

            <a
              href="#category-tree"
              className="rounded-full border border-white/60 px-6 py-3 font-bold text-white transition hover:bg-white/10"
            >
              View the Branches
            </a>
          </div>
        </div>
      </section>

      {/* Sticky Search / Category Jump */}
      <section className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="grid gap-3 lg:grid-cols-3 lg:items-center">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setExpandedCategories(new Set());
              }}
              placeholder="Find a function or area of service"
              className="h-12 rounded-full border-slate-300 pl-4 text-base"
              style={{ fontSize: "16px" }}
            />

            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => {
                  setActiveCategory("all");
                  setExpandedCategories(new Set());
                }}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition ${
                  activeCategory === "all"
                    ? "bg-royal-blue text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                All Branches
              </button>

              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => jumpToCategory(category.id)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition ${
                    activeCategory === category.id
                      ? "text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  style={
                    activeCategory === category.id
                      ? { backgroundColor: category.color }
                      : undefined
                  }
                >
                  {category.shortTitle}
                </button>
              ))}
            </div>

            <div className="flex gap-2 lg:justify-end">
              <button
                type="button"
                onClick={allVisibleExpanded ? collapseAll : expandVisible}
                className="whitespace-nowrap rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
              >
                {allVisibleExpanded ? "Collapse Results" : "Expand Results"}
              </button>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800"
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Structure Map */}
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-black text-royal-blue">
              <MapIcon className="h-4 w-4" />
              Structure Map
            </div>

            <h2 className="mt-4 font-heading text-3xl font-black text-slate-950">
              How the nation structure works
            </h2>

            <p className="mt-3 max-w-4xl text-slate-600">
              The page is not meant to be a long directory. It shows the order
              first, the branches second, and the specific functions last.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-4">
            {scripturalPatterns.map((item) => (
              <div
                key={item.reference}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
              >
                <p className="text-sm font-black text-royal-blue">
                  {item.reference}
                </p>

                <h3 className="mt-2 font-heading text-lg font-black text-slate-950">
                  {item.title}
                </h3>

                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-3xl border border-slate-200 bg-slate-50 p-5 md:p-6">
            <div className="mb-5 text-center">
              <p className="text-sm font-black uppercase tracking-wide text-royal-blue">
                Step 1
              </p>
              <h3 className="font-heading text-2xl font-black text-slate-950">
                Order functions guide the work
              </h3>
            </div>

            <div className="grid gap-4 md:grid-cols-5">
              {orderPrograms.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div key={item.key} className="relative">
                    <div className="h-full rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-slate-200">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-royal-blue text-white">
                        <Icon className="h-6 w-6" />
                      </div>

                      <h3 className="font-heading text-base font-black text-slate-950">
                        {item.title}
                      </h3>

                      <p className="mt-3 text-sm leading-6 text-slate-600">
                        {item.description}
                      </p>
                    </div>

                    {index < orderPrograms.length - 1 && (
                      <span className="mx-auto my-2 block text-center text-xl font-black text-slate-400 md:absolute md:-right-4 md:top-1/2 md:my-0 md:-translate-y-1/2">
                        →
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="my-8 flex flex-col items-center text-center">
              <div className="h-10 w-px bg-royal-blue/40" />
              <div className="rounded-full bg-royal-blue px-5 py-2 text-sm font-black text-white shadow-sm">
                Step 2: order feeds the branches
              </div>
              <div className="h-10 w-px bg-royal-blue/40" />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => {
                const Icon = category.icon;

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => jumpToCategory(category.id)}
                    className="group flex items-start gap-3 rounded-2xl bg-white p-4 text-left shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-md"
                  >
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white"
                      style={{ backgroundColor: category.color }}
                    >
                      <Icon className="h-5 w-5" />
                    </span>

                    <span>
                      <span className="block font-heading text-base font-black leading-tight text-slate-950">
                        {category.shortTitle}
                      </span>

                      <span className="mt-1 block text-sm text-slate-600">
                        {category.programs.length} functions inside
                      </span>

                      <span className="mt-2 block text-xs font-black text-royal-blue group-hover:underline">
                        Open branch →
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 rounded-2xl bg-white p-5 text-center ring-1 ring-slate-200">
              <p className="font-heading text-xl font-black text-slate-950">
                Step 3: open a branch and choose the function where you fit.
              </p>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                The Join / Volunteer button only appears on the specific
                function because that is where service becomes practical.
              </p>
            </div>
          </div>
        </section>

        {/* Where Do I Fit */}
        <section
          id="where-do-i-fit"
          className="mt-16 scroll-mt-32 rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-sm md:p-8"
        >
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-black text-kingdom-gold">
              <Compass className="h-4 w-4" />
              Where do I fit?
            </div>

            <h2 className="mt-4 font-heading text-3xl font-black">
              Choose what sounds like you.
            </h2>

            <p className="mt-4 max-w-3xl text-slate-300">
              There is one path here for each of the 9 main branches. Tap the
              one closest to your gift, skill, or area of readiness.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {fitOptions.map((option) => {
              const category = categories.find(
                (item) => item.id === option.categoryId
              );
              const Icon = category?.icon || Compass;

              return (
                <button
                  key={option.categoryId}
                  type="button"
                  onClick={() => chooseFit(option.categoryId, option.tag)}
                  className="group rounded-2xl border border-white/10 bg-white/5 p-5 text-left transition hover:border-kingdom-gold/70 hover:bg-white/10"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <span
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
                      style={{ backgroundColor: category?.color || "#1d4ed8" }}
                    >
                      <Icon className="h-5 w-5" />
                    </span>

                    <span className="text-sm font-black text-kingdom-gold">
                      {category?.shortTitle}
                    </span>
                  </div>

                  <p className="font-bold leading-6 text-white">
                    {option.title}
                  </p>

                  <span className="mt-4 block text-sm font-black text-kingdom-gold group-hover:underline">
                    Open this branch →
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Quick Filters */}
        <section
          id="quick-filters"
          className="mt-10 scroll-mt-32 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Compass className="h-5 w-5 text-royal-blue" />
                <h2 className="font-heading text-xl font-black text-slate-950">
                  Quick Filters
                </h2>
              </div>

              {activeTag !== "all" && (
                <p className="mt-2 text-sm font-semibold text-slate-600">
                  Showing matches for:{" "}
                  <span className="text-royal-blue">{activeTag}</span>
                </p>
              )}
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="w-fit rounded-full bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800"
              >
                Clear All Filters
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => applyTagFilter("all")}
              className={`rounded-full px-4 py-2 text-sm font-black transition ${
                activeTag === "all"
                  ? "bg-royal-blue text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              all
            </button>

            {quickFilters.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => applyTagFilter(tag)}
                className={`rounded-full px-4 py-2 text-sm font-black transition ${
                  activeTag === tag
                    ? "bg-royal-blue text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </section>

        {/* Category Tree */}
        <section id="category-tree" className="mt-16 scroll-mt-32">
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm font-black text-green-700">
              <Trees className="h-4 w-4" />
              Category Branches
            </div>

            <h2 className="mt-4 font-heading text-4xl font-black text-slate-950">
              The 9 main branches
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-slate-600">
              Each branch opens to show the functions inside. Search, filter, or
              jump by category to find your place faster.
            </p>
          </div>

          {filteredCategories.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <p className="font-heading text-2xl font-black text-slate-950">
                No matching branch found.
              </p>

              <p className="mt-3 text-slate-600">
                Clear the search or filters and try again.
              </p>

              <Button onClick={clearFilters} className="btn-gold mt-6">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {filteredCategories.map((category) => {
                const Icon = category.icon;
                const isExpanded = expandedCategories.has(category.id);
                const countLabel =
                  hasActiveFilters &&
                  category.visiblePrograms.length !== category.programs.length
                    ? `${category.visiblePrograms.length} Matches`
                    : `${category.programs.length} Functions`;

                return (
                  <article
                    id={category.id}
                    key={category.id}
                    className="scroll-mt-32 rounded-3xl border bg-white p-6 shadow-sm transition hover:shadow-xl"
                    style={{ borderColor: `${category.color}44` }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleCategory(category.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl text-white shadow-md"
                          style={{ backgroundColor: category.color }}
                        >
                          <Icon className="h-8 w-8" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <span
                              className="rounded-full px-3 py-1 text-xs font-black"
                              style={{
                                backgroundColor: category.softColor,
                                color: category.color,
                              }}
                            >
                              {countLabel}
                            </span>

                            {category.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>

                          <h3 className="font-heading text-2xl font-black leading-tight text-slate-950">
                            {category.title}
                          </h3>

                          <p className="mt-3 text-sm leading-6 text-slate-600">
                            {category.description}
                          </p>
                        </div>

                        <div className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                          <span className="text-lg font-black">
                            {isExpanded ? "−" : "+"}
                          </span>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="mt-6 border-t border-slate-200 pt-6">
                        <div className="grid gap-3">
                          {category.visiblePrograms.map((program) => {
                            const IconSmall = program.icon;

                            return (
                              <div
                                key={program.key}
                                className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
                              >
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                  <div className="flex gap-3">
                                    <div
                                      className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
                                      style={{
                                        backgroundColor: category.color,
                                      }}
                                    >
                                      <IconSmall className="h-5 w-5" />
                                    </div>

                                    <div>
                                      <h4 className="font-heading text-lg font-black text-slate-950">
                                        {program.name}
                                      </h4>

                                      <p className="mt-2 text-sm leading-6 text-slate-600">
                                        {program.description}
                                      </p>

                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {program.filterTags
                                          .slice(0, 4)
                                          .map((tag) => (
                                            <span
                                              key={tag}
                                              className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-600"
                                            >
                                              {tag}
                                            </span>
                                          ))}
                                      </div>
                                    </div>
                                  </div>

                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="btn-outline-royal whitespace-nowrap"
                                    onClick={() => setSelectedProgram(program)}
                                    data-testid={`program-join-${program.key}`}
                                  >
                                    Join / Volunteer
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* CTA */}
      <section className="bg-kingdom-gold py-12">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 font-heading text-2xl font-bold text-white">
            Not Sure Where to Start?
          </h2>

          <p className="mb-6 text-white/90">
            Contact us and we&apos;ll help you find the right function for your
            skills, service, and readiness.
          </p>

          <a href="mailto:admin@yasharal.vip">
            <Button className="bg-royal-blue text-white hover:bg-slate-800">
              Contact Us
            </Button>
          </a>
        </div>
      </section>

      <ProgramInterestModal
        program={selectedProgram}
        isOpen={!!selectedProgram}
        onClose={() => setSelectedProgram(null)}
      />
    </div>
  );
}
